import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl } from 'react-intl';

import getFormattedNumber from '../functions/getFormattedNumber';
import noticeError from '../functions/noticeError';
import useForm from '../hooks/useForm';
import formats from '../definitions/Formats';
import Colours from '../definitions/Colours';
import TooltipAnchor from './TooltipAnchor';
import { BOTTOM, MIDDLE } from './Tooltip';
import CondensedTheme from './CondensedTheme';
import InputSelector from './InputSelector';


function CalculationList({ variant, lines, result, ...otherProps }) {
    const form = useForm();
    const intl = useIntl();

    if (form == null) {
        const error = new Error('[CalculationList] This component must be used within a <Form>');

        noticeError(error);
        throw error;
    }

    // Register any computeds to the form
    useEffect(() => {
        const computeds = lines.filter(l => typeof l.value === 'function');

        computeds.forEach(c => {
            if (!c.name) {
                const error = new Error('[CalculationList] Attempted to add a computed but no name was found.');
                
                noticeError(error, {
                    computeds,
                });
                throw error;
            }

            form.addComputed(c.name, c.value);
        });

        // Dispose of the computed when the component is unmounted
        return () => computeds.forEach(c => form.removeComputed(c.name));
    }, [ form, lines ]);

    const isDashedVariant = variant === VARIANTS.dashed;

    return (
        <CondensedTheme>
            <Container {...otherProps}>
                <For
                    each="line"
                    of={lines}
                >
                    <If condition={isDashedVariant}>
                        <span className={
                            classNames('operator', 'weight-medium',
                                !line?.isSubLine ? 'col1' : 'col2 rh-ml-2',
                            )}
                        >
                            {OPERATORS[line?.operator]}
                        </span>

                        <div className={classNames('content', !line?.isSubLine ? 'span2' : 'col3')}>
                            <If condition={line?.tooltipMessage}>
                                <TooltipAnchor
                                    className="tooltip rh-mr-1"
                                    message={line.tooltipMessage}
                                    tooltipWidth="30rem"
                                    tooltipLocation={BOTTOM}
                                    nubbinAlignment={MIDDLE}
                                />
                            </If>

                            <span className={
                                classNames('label', 'rh-my-0_5', 'rh-mr-1',
                                    { 'rh-pl-2': line?.isSubLine },
                                    { 'rh-title-xs': !line.isSubLine },
                                )}
                            >
                                {line.label}
                            </span>

                            <hr className="divider rh-mr-1" />
                        </div>

                        <div className="valueContainer col4">
                            <Choose>
                                <When condition={typeof line.value === 'object'}>
                                    <InputSelector
                                        className="rh-my-0_5 rh-pl-1"
                                        field={line.value}
                                        type="currency"
                                        placeholder={(
                                            <FormattedMessage
                                                id="base-ui.CalculationList.amountPlaceholder"
                                                defaultMessage="$ Enter amount"
                                            />
                                        )}
                                    />
                                </When>
                                <Otherwise>
                                    <span className="value rh-my-0_5 rh-pl-1">
                                        {getFormattedNumber(getFormValueByType(line.value, form), line?.format ?? 'CAD0', intl)}
                                    </span>
                                </Otherwise>
                            </Choose>
                        </div>
                    </If>
                </For>

                <If condition={result}>
                    <span className="operator col1 rh-my-0_5 rh-text-l weight-medium">
                        {OPERATORS[result?.operator]}
                    </span>

                    <span className="label span2 rh-title-xs rh-my-0_5 weight-medium">
                        {result.label}
                    </span>

                    <p className="resultValue col4 rh-title-m rh-my-0_5 rh-pl-1 weight-medium">
                        {getFormattedNumber(getFormValueByType(result.value, form), 'CAD0', intl)}
                    </p>
                </If>
            </Container>
        </CondensedTheme>
    );
}

const VARIANTS = {
    standard: 'standard',
    dashed: 'dashed',
};

const OPERATOR_TYPES = {
    NONE: 'none',
    PLUS: 'plus',
    MINUS: 'minus',
    TIMES: 'times',
    EQUALS: 'equals',
};

const OPERATORS = {
    [OPERATOR_TYPES.NONE]: '',
    [OPERATOR_TYPES.PLUS]: '+',
    [OPERATOR_TYPES.MINUS]: '−',
    [OPERATOR_TYPES.TIMES]: '×',
    [OPERATOR_TYPES.EQUALS]: '=',
};

CalculationList.propTypes = {
    variant: PropTypes.oneOf(Object.values(VARIANTS)),
    lines: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            operator: PropTypes.oneOf(Object.values(OPERATOR_TYPES)),
            value: PropTypes.oneOfType([
                PropTypes.string, // Name of the registered form value
                PropTypes.number, // If the value isn't derived from anything
                PropTypes.func, // A possible equation to be added as a computed within the form
                PropTypes.object, // A field object used to render an input
            ]).isRequired,
            format: PropTypes.oneOf(Object.keys(formats.number)),
            isSubLine: PropTypes.bool,
            label: PropTypes.node.isRequired,
            tooltipMessage: PropTypes.node,
        }),
    ).isRequired,
    result: PropTypes.shape({
        name: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
        value: PropTypes.oneOfType([
            PropTypes.string, // Name of the registered form value
            PropTypes.number, // If the value isn't derived from anything
            PropTypes.func, // A possible equation to be added as a computed within the form
        ]).isRequired,
        operator: PropTypes.oneOf(Object.values(OPERATOR_TYPES)),
    }),
};

CalculationList.defaultProps = {
    variant: VARIANTS.standard,
    result: undefined,
};

function getFormValueByType(value, form) {
    // Invoke the passed lambda with form values
    if (typeof value === 'function') {
        return value(form.values);
    }

    // Treated as a field object so get the value property
    if (typeof value === 'object') {
        return form.getValue(value.value);
    }

    // An existing form name
    if (typeof value === 'string') {
        return form.getValue(value);
    }

    // Was just passed a static value
    return value;
}

const Container = styled.div`
    display: grid;
    grid-template-columns: minmax(0, min-content) minmax(0, min-content) 1fr max-content;
    grid-auto-rows: 1fr;
    align-items: center;

    .operator {
        color: ${Colours.BLUEBERRY_DARK};
    }

    .col1 { grid-column-start: 1; }
    .col2 { grid-column-start: 2; }
    .col3 { grid-column-start: 3; }
    .col4 { grid-column-start: 4; }
    .span2 { grid-column: 2 / 4; }

    .content {
        display: flex;
        align-items: center;

        .label {
            flex-shrink: 1;
        }

        .divider {
            flex: 1;
            height: 1px;

            border: 0;
            border-top: 1px dashed ${Colours.STONE_DARK};
        }
    }

    .valueContainer {
        input {
            max-width: 9rem;
            min-width: 7rem;
        }
    }

    .resultValue {
        color: ${Colours.BLUEBERRY_DARK};
    }
`;

export default observer(CalculationList);
